import React, { useState, useContext, useMemo } from 'react';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import '../css/pors/doc_process.css';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Table, Form, InputGroup, Pagination } from 'react-bootstrap';
import { MdEdit, MdBlockFlipped, MdSendAndArchive, MdSearch, MdUnfoldMore, MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { ToastContainer, toast, Zoom } from 'react-toastify';
import { Editec } from '../edit/editEc';
import { ReasonEc } from '../reason/reason_ec';
import api from '../../api';
import { Dimcontext } from "../../context/pagecontext";
import { AuthContext } from '../../context/authContext';
import Swal from 'sweetalert2';
import { Datacontext } from '../../context/dataContext';

const HomeEC = () => {
  const { setDim } = useContext(Dimcontext);
  const { user } = useContext(AuthContext);
  const { ecs, fetchEc } = useContext(Datacontext);

  const [docedit, set_docedit] = useState(false);
  const [docid, set_docid] = useState('');
  const [reason, set_reason] = useState(false);
  const [appno, set_appno] = useState('');

  // --- Pagination & Search States ---
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: 'application_no', direction: 'desc' });

  const showToast = (msg) => 
    toast.warn(msg, {
      position: "bottom-center",
      autoClose: 3000,
      theme: "dark",
      transition: Zoom,
    });

  const showsToast = (msg) => 
    toast.success(msg, {
      position: "bottom-center",
      autoClose: 3000,
      theme: "dark",
      transition: Zoom,
    });

  const docs = useMemo(() => {
    if (!ecs) return [];
    const processingDocs = ecs.filter(d => d.docstatus === "processing");

    if (user?.role === "user") {
      return processingDocs.filter(d => 
        // FIXED: Safe array fallback prevents .includes() from crashing on undefined
        (d?.managed_By || []).map(name => name.toLowerCase()).includes(user.name.toLowerCase())
      );
    }
    return processingDocs;
  }, [ecs, user]);

  // --- Optimized Filtering & Sorting ---
  const processedData = useMemo(() => {
    let filtered = docs.filter(doc => {
      const ownerName = doc.owner?.name || "";
      const searchStr = `${ownerName} ${doc.application_no} ${doc.register_office}`.toLowerCase();
      return searchStr.includes(searchTerm.toLowerCase());
    });

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        // Logic to extract values based on key (Handling nested objects)
        if (sortConfig.key === 'name') {
          aValue = a.owner?.name || "";
          bValue = b.owner?.name || "";
        } else if (sortConfig.key === 'status') {
          // Extract nested status string for sorting
          aValue = a.status?.status || "";
          bValue = b.status?.status || "";
        } else {
          aValue = a[sortConfig.key] || "";
          bValue = b[sortConfig.key] || "";
        }

        const strA = String(aValue).toLowerCase();
        const strB = String(bValue).toLowerCase();

        if (strA < strB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (strA > strB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [docs, searchTerm, sortConfig]);

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = processedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(processedData.length / rowsPerPage);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const handleAction = (type, id, app) => {
    set_docid(id);
    setDim(true);
    if (type === 'edit') set_docedit(true);
    if (type === 'reject') { set_reason(true); set_appno(app); }
  };

  // This helper checks if the specific document has reached 100%
  const isProcessFinished = (doc) => {
    return calculateProgress(doc) === 100;
  };

  const handlecomplete = async (id) => {
    // Find the specific document from your ecs array
    const doc = ecs.find(item => item.id === id);
    
    // Check if the calculation hits 100%
    if (isProcessFinished(doc)) {
      try {
        const result = await Swal.fire({
          title: 'Finalize Application?',
          text: "This will move the document to the completed archive.",
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#8a2be2',
          cancelButtonColor: '#d33',
          confirmButtonText: 'Yes, Complete it!'
        });

        if (result.isConfirmed) {
          await api.patch(`/updateone_ec/${id}`, { 
            data1: { docstatus: "completed" } 
          });
          toast.success("Application marked as Completed!");
          fetchEc();
        }
      } catch (error) { 
        console.log(error); 
        toast.error("Failed to update status");
      }
    } else {
      // Show how much is left to do
      const currentProgress = calculateProgress(doc);
      Swal.fire({ 
        title: "INCOMPLETE", 
        html: `Current Progress: <b>${currentProgress}%</b><br/>Please fill all mandatory fields and document toggles before completing.`, 
        icon: "warning", 
        confirmButtonColor: "#e7ac2d"
      });
    }
  };

  const getBadgeColor = (name) => {
    const variants = ['primary', 'secondary', 'success', 'danger', 'warning', 'info'];
    return variants[name.length % variants.length];
  };

  const calculateProgress = (doc) => {
    if (!doc) return 0;

    // Step 1: Application & Executive Details (5 Fields)
    const step1Fields = [
      !!doc.zone,
      !!doc.register_office,
      doc.managed_By && doc.managed_By.length > 0,
      !!doc.refered_by,
      !!doc.owner
    ];

    // Step 2: Document Process (4 Fields)
    const isOnline = doc.status?.status?.toLowerCase() === 'online';

    // Logic for Primary Document (ECA No for Online / Payment for Offline)
    let primaryDocValid = false;
    if (isOnline) {
      // Valid if value is 'no' OR (value is 'yes' AND ecno.no exists)
      primaryDocValid = doc.ecno?.values === 'no' || (doc.ecno?.values === 'yes' && !!doc.ecno?.no);
    } else {
      // FIXED: Added optional chaining to prevent undefined crash
      const ecno = !!doc.ecno?.values;
      const pay = doc.payment?.values === 'no' || (doc.payment?.values === 'yes' && !!doc.payment?.val)
      primaryDocValid = ecno && pay
    }

    const step2Fields = [
      primaryDocValid,
      !!doc.ec_received?.values,
      !!doc.ec_delivered?.values,
      !!doc.estimated_cost
    ];

    // Combine and calculate
    const allFields = [...step1Fields, ...step2Fields];
    const completedCount = allFields.filter(Boolean).length;
    const totalCount = allFields.length;

    return Math.floor((completedCount / totalCount) * 100);
  };

  return (
    <div className="home-layout-wrapper p-0 p-md-4">
      <ToastContainer />

      <div className="main-glass-card shadow-sm">
        <div className="card-header-custom d-flex justify-content-between align-items-center p-4">
          <h4 className="m-0 fw-bold text-dark">Processing Encumbrance Certificate (EC)</h4>
          <div className="d-flex gap-3">
            <InputGroup className="search-bar">
              <InputGroup.Text className="bg-white border-end-0">
                <MdSearch size={20} className="text-muted" />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search name, app no..."
                className="border-start-0 ps-0"
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </InputGroup>
          </div>
        </div>

        <div className="table-side-scroll-container">
          <Table hover striped responsive className="imtable">
            <thead>
              <tr>
                <th className="sticky-col pin-no">NO.</th>
                <th className="sticky-col pin-name cursor-pointer" onClick={() => handleSort('name')}>
                  NAME <MdUnfoldMore />
                </th>
                <th className="cursor-pointer" onClick={() => handleSort('application_no')}>
                  APP NO <MdUnfoldMore />
                </th>
                <th className="cursor-pointer" onClick={() => handleSort('status')}>
                  TYPE <MdUnfoldMore />
                </th>
                <th className="cursor-pointer" onClick={() => handleSort('document_date')}>
                  DATE <MdUnfoldMore />
                </th>
                <th className="cursor-pointer" onClick={() => handleSort('register_office')}>
                  OFFICE <MdUnfoldMore />
                </th>
                <th>MANAGED BY</th>
                <th>PROCESS</th>
                <th className="text-center">ACTION</th>
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? currentRows.map((doc, index) => (
                <tr key={doc.id}>
                  <td className="sticky-col pin-no">{indexOfFirstRow + index + 1}</td>
                  <td className="sticky-col pin-name fw-semibold">{doc.owner?.name?.toUpperCase() || "N/A"}</td>
                  <td><span className="app-badge">{doc.application_no}</span></td>
                  <td>
                    <div className="d-flex justify-content-center">
                      <span 
                        className={`badge rounded-pill bg-${doc?.status?.status?.toLowerCase() === 'online' ? 'success' : 'secondary'}`} 
                        style={{ fontSize: '0.60rem', minWidth: '55px' }}
                      >
                        {doc?.status?.status?.toUpperCase() || "N/A"}
                      </span>
                    </div>
                  </td>
                  
                  {/* FIXED: Safe Date Parsing */}
                  <td>{doc.date ? new Date(doc.date.split("-").reverse().join("-")).toLocaleDateString("en-GB") : "N/A"}</td>
                  
                  <td>{doc.register_office?.toUpperCase() || "N/A"}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-1 justify-content-center">
                      {/* FIXED: Array.isArray prevents mapping over strings/undefined */}
                      {Array.isArray(doc.managed_By) && doc.managed_By.map((manager, i) => (
                        <span key={i} className={`badge rounded-pill bg-${getBadgeColor(manager)}`} style={{ fontSize: '0.60rem' }}>
                          {manager?.toUpperCase()}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <div style={{ width: 45, height: 45 }}>
                      <CircularProgressbar
                        value={calculateProgress(doc)}
                        text={`${calculateProgress(doc)}%`}
                        styles={buildStyles({
                          textSize: '28px',
                          pathColor: '#4a127e',
                          textColor: '#4a127e',
                          trailColor: '#f1f1f1'
                        })}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex justify-content-center gap-2">
                      <Button className="btn-action be" onClick={() => handleAction('edit', doc.id)}><MdEdit /></Button>
                      <Button className="btn-action bp" onClick={() => handlecomplete(doc.id)}><MdSendAndArchive /></Button>
                      <Button className="btn-action br" onClick={() => handleAction('reject', doc.id, doc.application_no)}><MdBlockFlipped /></Button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={9} className="text-center py-5 text-muted">No processing documents found</td></tr>
              )}
            </tbody>
          </Table>
        </div>

        <div className="pagination-footer d-flex flex-column flex-md-row justify-content-between align-items-center p-3 border-top bg-light gap-3">
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">Rows per page:</span>
            <Form.Select 
              size="sm" 
              style={{ width: '80px', borderRadius: '6px' }}
              value={rowsPerPage} 
              onChange={(e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              {[5, 10, 20, 50].map(v => <option key={v} value={v}>{v}</option>)}
            </Form.Select>
          </div>

          <div className="text-muted small fw-medium">
            {processedData.length > 0 ? (
              <>Showing <b>{indexOfFirstRow + 1}</b> to <b>{Math.min(indexOfLastRow, processedData.length)}</b> of <b>{processedData.length}</b> entries</>
            ) : "No entries found"}
          </div>

          <Pagination className="mb-0 custom-pagination shadow-sm">
            <Pagination.First disabled={currentPage === 1} onClick={() => setCurrentPage(1)} />
            <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}>
              <MdKeyboardArrowLeft size={20} />
            </Pagination.Prev>
            <Pagination.Item active className="px-2">{currentPage}</Pagination.Item>
            <Pagination.Next disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}>
              <MdKeyboardArrowRight size={20} />
            </Pagination.Next>
            <Pagination.Last disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(totalPages)} />
          </Pagination>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {docedit && (
          <motion.div key="edit-modal" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} className="modal-overlay-wrapper">
            <Editec set_docedit={set_docedit} id={docid} showToast={showToast} onclose={fetchEc} />
          </motion.div>
        )}

        {reason && (
          <motion.div key="reason-modal" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 30 }} className="modal-overlay-wrapper">
            <ReasonEc set_reason={set_reason} id={docid} appno={appno} showsToast={showsToast} showToast={showToast} onclose={fetchEc} TitleCase={(s) => s} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HomeEC;